/** The tour operator's Defaults tab (backend plan 2026-10-07-console-operator-defaults-tab):
 *
 *  - `/tour_operators/{id}/default_tag_categories/` — GET (`console_top_view`) / PUT
 *    (`console_top_manage`): the operator's default tag categories, the franchise parent's
 *    rows a licensee inherits, a version token, and who receives them. The PUT replaces
 *    the whole list (list position = sequence) and answers with the GET shape; 409
 *    `defaults_modified` when `version` is stale, 400 `{detail, rows: {"<i>": {field: msg}}}`
 *    for row problems (`<i>` indexes the PUT's `rows`).
 *  - `/tour_operators/{id}/trip_defaults/` — GET / PATCH: the config-stored trip defaults,
 *    each with its own value, the value tours get and where it comes from; the PATCH sets /
 *    unsets dotted paths (`if_unmodified_since` = the GET's `config_last_modified_ts`) and
 *    answers with the GET shape; 409 `config_modified` (the tour-operator PATCH's shape).
 *
 *  Shapes come from the generated spec types; never hand-mirror them. */
import { apiGet, apiPatch, apiPut, ConsoleApiError } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type OperatorDefaultTagCategories = Schemas['ConsoleOperatorDefaultTagCategories'];
export type OperatorDefaultTagCategoryRow = Schemas['ConsoleOperatorDefaultTagCategoryRow'];
export type OperatorDefaultTagCategoryInheritedRow =
  Schemas['ConsoleOperatorDefaultTagCategoryInheritedRow'];
export type OperatorDefaultTagCategoryWriteRow =
  Schemas['ConsoleOperatorDefaultTagCategoryWriteRow'];
export type OperatorDefaultTagCategoriesWrite = Schemas['ConsoleOperatorDefaultTagCategoriesWrite'];
export type OperatorDefaultTagCategoriesRowErrors =
  Schemas['ConsoleOperatorDefaultTagCategoriesRowErrors'];
export type OperatorResolvedFlag = Schemas['ConsoleOperatorResolvedFlag'];

export type OperatorTripDefaults = Schemas['ConsoleOperatorTripDefaults'];
export type OperatorTripDefaultSetting = Schemas['ConsoleOperatorTripDefaultSetting'];
export type OperatorTripDefaultKey = OperatorTripDefaultSetting['key'];
export type OperatorTripDefaultSource = OperatorTripDefaultSetting['source'];
export type OperatorTripDefaultsPatch = Schemas['ConsoleOperatorTripDefaultsPatch'];

/** Query keys — under the operator, so `['tourOperators']` invalidations reach them. */
export const defaultTagCategoriesKey = (topId: number) =>
  ['tourOperators', topId, 'defaultTagCategories'] as const;
export const tripDefaultsKey = (topId: number) => ['tourOperators', topId, 'tripDefaults'] as const;

export function getDefaultTagCategories(
  topId: number,
  signal?: AbortSignal,
): Promise<OperatorDefaultTagCategories> {
  return apiGet<OperatorDefaultTagCategories>(`/tour_operators/${topId}/default_tag_categories/`, {
    signal,
  });
}

export function putDefaultTagCategories(
  topId: number,
  body: OperatorDefaultTagCategoriesWrite,
): Promise<OperatorDefaultTagCategories> {
  return apiPut<OperatorDefaultTagCategories>(
    `/tour_operators/${topId}/default_tag_categories/`,
    body,
  );
}

export function getTripDefaults(
  topId: number,
  signal?: AbortSignal,
): Promise<OperatorTripDefaults> {
  return apiGet<OperatorTripDefaults>(`/tour_operators/${topId}/trip_defaults/`, { signal });
}

export function patchTripDefaults(
  topId: number,
  body: OperatorTripDefaultsPatch,
): Promise<OperatorTripDefaults> {
  return apiPatch<OperatorTripDefaults>(`/tour_operators/${topId}/trip_defaults/`, body);
}

/** Someone saved the default tag categories after `version` was read (409
 *  `defaults_modified`). Not a type predicate (see `isStatementTimeout`). */
export function isDefaultsModified(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 409 && e.body?.code === 'defaults_modified';
}

/** The PUT's row-errors 400, or null for any other failure (an envelope 400 — a short
 *  reason — has DRF's field-error shape and no `rows`). */
export function defaultsRowErrors(e: unknown): OperatorDefaultTagCategoriesRowErrors | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return null;
  const body = e.body as Partial<OperatorDefaultTagCategoriesRowErrors> | undefined;
  if (!body || typeof body.rows !== 'object' || body.rows === null) return null;
  return { detail: body.detail ?? '', rows: body.rows };
}
